/**
 * Cert expiry watchlist — só renderiza se há certs expirando em <90d.
 *
 * Visual: Card com border-left amber (.certWatchCard). Tabela interna
 * lista hostname, ARN truncado, dias restantes (cor por urgência),
 * status auto-renew. Ações por enquanto são link "Ver app".
 */
import { Badge, Card, Group, Table, Text } from '@mantine/core'
import { IconCertificate } from '@tabler/icons-react'
import dayjs from 'dayjs'
import type { CertExpiryItem } from '../types'
import styles from '../ZtnaAdmin.module.css'

interface Props {
  items: CertExpiryItem[]
}

function urgencyColor(days: number | null): string | undefined {
  if (days == null) return undefined
  if (days < 30) return 'var(--ai-danger, #e03131)'
  if (days < 60) return 'var(--ai-warning, #f59f00)'
  return undefined
}

function shortArn(arn: string): string {
  // arn:aws:acm:us-east-1:123456789012:certificate/abc-def
  const parts = arn.split('/')
  const tail = parts[parts.length - 1]
  return tail ? `…/${tail.slice(0, 12)}` : arn.slice(-32)
}

export function CertExpiryWatchlist({ items }: Props) {
  if (!items || items.length === 0) return null
  return (
    <Card className={styles.certWatchCard}>
      <Group mb="sm" gap="xs">
        <IconCertificate size={18} color="var(--ai-warning, #f59f00)" />
        <Text fw={700}>Cert expiry watchlist</Text>
        <Text size="xs" c="dimmed">
          {items.length} certificado{items.length === 1 ? '' : 's'} expirando em &lt;90 dias
        </Text>
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Hostname</Table.Th>
            <Table.Th>ARN</Table.Th>
            <Table.Th>Expira em</Table.Th>
            <Table.Th>Dias restantes</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((c) => (
            <Table.Tr key={c.application_id}>
              <Table.Td>
                <Text size="sm" fw={600}>
                  {c.slug}
                </Text>
                <Text size="xs" c="dimmed" ff="monospace">
                  {c.hostname}
                </Text>
              </Table.Td>
              <Table.Td ff="monospace" fz="xs">
                {shortArn(c.cert_arn)}
              </Table.Td>
              <Table.Td ff="monospace" fz="xs">
                {c.expires_at ? dayjs(c.expires_at).format('YYYY-MM-DD') : '—'}
              </Table.Td>
              <Table.Td>
                <Badge
                  variant="light"
                  color={
                    c.days_remaining != null && c.days_remaining < 30
                      ? 'red'
                      : c.days_remaining != null && c.days_remaining < 60
                        ? 'yellow'
                        : 'gray'
                  }
                  style={{ color: urgencyColor(c.days_remaining) }}
                >
                  {c.days_remaining != null ? `${c.days_remaining}d` : '—'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text
                  component="a"
                  href={`/auditoria-global?resource_type=application&resource_id=${c.application_id}`}
                  size="xs"
                  c="inovareTeal.7"
                  style={{ textDecoration: 'underline' }}
                >
                  Ver app
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

export default CertExpiryWatchlist
